import {
  Component, ChangeDetectionStrategy, Input, ViewChild, ElementRef, EventEmitter, Output, ChangeDetectorRef, DestroyRef, inject,
} from '@angular/core'
import {
  FormGroup, Validators, FormControl, ReactiveFormsModule,
} from '@angular/forms'
import { RegionSelectIntl } from './region-select.intl'

import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon'
import { CardComponent } from '@knorr-bremse-portals/ngx-components/components/card'
import { ButtonComponent } from '@knorr-bremse-portals/ngx-components/components/button'
import { LabelComponent } from '@knorr-bremse-portals/ngx-components/components/label'
import {
  SelectComponent, SelectOptionComponent,
} from '@knorr-bremse-portals/ngx-components/components/controls/select'
import { ErrorComponent } from '@knorr-bremse-portals/ngx-components/components/controls/error'
import { Language } from '@knorr-bremse-portals/ngx-components/components/language-select'
import { iconClose } from '@knorr-bremse-portals/icons'

export interface Region {
  code: string
  name: string
  languages: Language[]
}

@Component({
  selector: 'kb-region-select',
  templateUrl: './region-select.component.html',
  styleUrls: ['./region-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, SelectComponent, SelectOptionComponent, CardComponent, ButtonComponent, ErrorComponent, LabelComponent, IconComponent],
})
export class RegionSelectComponent {
  protected iconClose = iconClose

  protected form = new FormGroup({
    region: new FormControl<Region | null>(null, {
      validators: Validators.required,
      nonNullable: false,
    }),
    language: new FormControl<Language | null>(null, {
      validators: Validators.required,
      nonNullable: false,
    }),
  })

  protected activeRegion: Region | null = null
  protected activeLanguage: Language | null = null

  protected regionComparator = (region1: Region | null, region2: Region | null) => region1?.code === region2?.code

  protected languageComparator = (language1: Language | null, language2: Language | null) => language1?.code === language2?.code

  @Input()
  public regions!: Region[]

  @Input()
  public set selectedRegion(region: Region | null) {
    this.form.controls.region.setValue(region)
    this.activeRegion = region
  }

  public get selectedRegion(): Region | null {
    return this.form.controls.region.value
  }

  @Input()
  public set selectedLanguage(language: Language | null) {
    this.form.controls.language.setValue(language)
    this.activeLanguage = language
  }

  public get selectedLanguage(): Language | null {
    return this.form.controls.language.value
  }

  @Output()
  public readonly ngSubmit = new EventEmitter<{
    region: Region
    language: Language
  }>()

  @ViewChild('dialog') private dialogElement!: ElementRef<HTMLDialogElement>

  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)
  protected intl = inject(RegionSelectIntl)

  public constructor() {
    this.intl.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck())

    this.form.controls.region.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((region) => {
      if (region && !region.languages.find(lang => lang.code === this.selectedLanguage?.code)) {
        this.form.controls.language.setValue(null)
      }
    })
  }

  public open(): void {
    this.dialogElement?.nativeElement.showModal()
  }

  public close(): void {
    this.dialogElement?.nativeElement.close()
  }

  protected onSubmit(): void {
    if (this.form.invalid) {
      return
    }

    this.ngSubmit.emit({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      region: this.selectedRegion!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      language: this.selectedLanguage!,
    })
    this.activeRegion = this.selectedRegion
    this.activeLanguage = this.selectedLanguage

    this.close()
  }
}
